import { useEffect, useState } from 'react';

import { ExportCsvButton } from '../../components/ui/ExportCsvButton.jsx';
import { SearchableSelect } from '../../components/ui/SearchableSelect.jsx';
import { ExclusionReasonSelect } from './ExclusionReasonSelect.jsx';
import { GapTypeChips } from './GapTypeChips.jsx';

const SORT_OPTIONS = [
  { value: 'default',                       label: 'Tab default' },
  { value: 'total_amount|desc',             label: 'Biggest $ first' },
  { value: 'total_amount|asc',              label: 'Smallest $ first' },
  { value: 'billable_hours_at_stake|desc',  label: 'Biggest fix-impact first (hrs at stake)' },
  { value: 'gap_count|desc',                label: 'Most gaps first' },
  { value: 'is_customer_ot|desc',           label: 'Customer OT first' },
  { value: 'business_date|desc',            label: 'Newest date first' },
  { value: 'business_date|asc',             label: 'Oldest date first' },
  { value: 'gross_hours|desc',              label: 'Most hours first' },
  { value: 'employee_name|asc',             label: 'Employee A→Z' },
  { value: 'ns_location_name|asc',          label: 'Site A→Z' },
  { value: 'pay_group|asc',                 label: 'Pay Group A→Z' },
  { value: 'pay_group|desc',                label: 'Pay Group Z→A' },
  { value: 'id|desc',                       label: 'Most recently processed' },
];

export function RecordsFilters({
  filter,
  search, setSearch,
  dateStart, setDateStart, dateEnd, setDateEnd,
  siteCode, setSiteCode,
  billingTitleId, setBillingTitleId,
  payGroup, setPayGroup,
  customerOt, setCustomerOt,
  statusAll, setStatusAll,
  gapTypes, toggleGapType,
  exclusionReason, setExclusionReason,
  pushEligibleOnly, setPushEligibleOnly,
  // Column + direction always move together — one setter, and (null, null)
  // means "drop the sort from the URL and fall back to this tab's default".
  sortBy, sortDir, setSort,
  facets, facetsLoading, recordsShown, total,
  onClearAll,
  // Export covers the whole filter (or the whole selection), not the rows
  // rendered — the label says which, so nobody has to guess.
  onExport, exportLabel = 'Export CSV', exportDisabled = false,
}) {
  const hasAnyActive =
    search || dateStart || dateEnd || siteCode || billingTitleId || payGroup || customerOt
    || gapTypes.size > 0 || exclusionReason || pushEligibleOnly || statusAll;

  // Dates are committed on blur, not per keystroke: the visible inputs are
  // driven by local drafts and only push up to the committed dateStart/dateEnd
  // (which actually trigger fetches) when the field loses focus or Enter is
  // pressed. This is what makes the search fire "once we leave the box" instead
  // of firing on every partial date as the user types the year. The effects
  // below re-sync the drafts when the committed value changes externally
  // (Clear All, deep-link intent).
  const [draftStart, setDraftStart] = useState(dateStart);
  const [draftEnd,   setDraftEnd]   = useState(dateEnd);
  useEffect(() => { setDraftStart(dateStart); }, [dateStart]);
  useEffect(() => { setDraftEnd(dateEnd); }, [dateEnd]);
  const commitStart = () => { if (draftStart !== dateStart) setDateStart(draftStart); };
  const commitEnd   = () => { if (draftEnd !== dateEnd) setDateEnd(draftEnd); };

  return (
    <>
      <div style={{
        display: 'flex', gap: 12, marginBottom: 8,
        flexWrap: 'wrap', alignItems: 'center',
      }}>
        <div className="form-group" style={{ margin: 0 }}>
          <label style={{ fontSize: 11 }}>Search</label>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Employee, site, punch ID..."
            style={{ width: 220 }}
          />
        </div>
        <div className="form-group" style={{ margin: 0 }}>
          <label style={{ fontSize: 11 }}>Date From</label>
          <input
            type="date" value={draftStart}
            onChange={(e) => setDraftStart(e.target.value)}
            onBlur={commitStart}
            onKeyDown={(e) => { if (e.key === 'Enter') commitStart(); }}
            style={{ width: 150 }}
          />
        </div>
        <div className="form-group" style={{ margin: 0 }}>
          <label style={{ fontSize: 11 }}>Date To</label>
          <input
            type="date" value={draftEnd}
            onChange={(e) => setDraftEnd(e.target.value)}
            onBlur={commitEnd}
            onKeyDown={(e) => { if (e.key === 'Enter') commitEnd(); }}
            style={{ width: 150 }}
          />
        </div>
        {hasAnyActive && (
          <button
            className="btn btn-primary"
            onClick={onClearAll}
            style={{ alignSelf: 'flex-end' }}
          >
            Clear All
          </button>
        )}
        {onExport && (
          <ExportCsvButton
            onExport={onExport}
            label={exportLabel}
            disabled={exportDisabled}
            title="Download every record matching this filter (or the whole selection) as CSV — not just the rows loaded here"
            style={{ alignSelf: 'flex-end', paddingBottom: 2 }}
          />
        )}
        <div style={{ fontSize: 12, color: '#666', alignSelf: 'flex-end', paddingBottom: 4 }}>
          Showing {recordsShown} of {total.toLocaleString()}
        </div>
      </div>

      <div style={{
        display: 'flex', gap: 12, marginBottom: 8,
        flexWrap: 'wrap', alignItems: 'center',
      }}>
        <div className="form-group" style={{ margin: 0 }}>
          <label style={{ fontSize: 11 }}>Location</label>
          <SearchableSelect
            value={siteCode}
            onChange={setSiteCode}
            options={(facets?.sites || []).map((s) => ({
              value: s.site_code, label: s.name, count: s.count,
            }))}
            placeholder="All locations — type to search"
            width={280}
          />
        </div>
        <div className="form-group" style={{ margin: 0 }}>
          <label style={{ fontSize: 11 }}>Billing Title</label>
          <SearchableSelect
            value={billingTitleId}
            onChange={setBillingTitleId}
            options={(facets?.billing_titles || []).map((t) => ({
              value: t.id, label: t.name, count: t.count,
            }))}
            placeholder="All titles — type to search"
            width={240}
          />
        </div>
        {/* Dayforce pay group (payroll calendar). The value sent to the API is
            the xref code; the label leads with the short code billing uses. */}
        <div className="form-group" style={{ margin: 0 }}>
          <label style={{ fontSize: 11 }}>Pay Group</label>
          <SearchableSelect
            value={payGroup}
            onChange={setPayGroup}
            options={(facets?.pay_groups || []).map((g) => ({
              value: g.xref_code,
              label: g.short_code === g.name ? g.short_code : `${g.short_code} — ${g.name}`,
              count: g.count,
            }))}
            placeholder="All pay groups — type to search"
            width={260}
          />
        </div>
        <div className="form-group" style={{ margin: 0 }}>
          <label style={{ fontSize: 11 }}>Customer OT</label>
          <select
            value={customerOt}
            onChange={(e) => setCustomerOt(e.target.value)}
            style={{ width: 130 }}
          >
            <option value="">Any</option>
            <option value="true">OT only ({facets?.customer_ot?.on?.toLocaleString() || 0})</option>
            <option value="false">No OT ({facets?.customer_ot?.off?.toLocaleString() || 0})</option>
          </select>
        </div>
        <div className="form-group" style={{ margin: 0 }}>
          <label style={{ fontSize: 11 }}>Sort by</label>
          <select
            value={`${sortBy}|${sortDir}`}
            onChange={(e) => {
              if (e.target.value === 'default') {
                setSort(null, null);
              } else {
                const [b, d] = e.target.value.split('|');
                setSort(b, d);
              }
            }}
            style={{ width: 210 }}
          >
            {SORT_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
        {filter === 'all' && (
          <div className="form-group" style={{ margin: 0 }}>
            <label style={{ fontSize: 11 }}>Status</label>
            <select
              value={statusAll}
              onChange={(e) => setStatusAll(e.target.value)}
              style={{ width: 180 }}
            >
              <option value="">Any status</option>
              <option value="pending">Pending</option>
              <option value="gap">Gap</option>
              <option value="excluded_ref_data">Excluded (ref data)</option>
              <option value="approved">Approved</option>
              <option value="queued">Queued</option>
              <option value="pushed">Synced</option>
              <option value="source_deleted">Ops Review — Source Deleted</option>
              <option value="source_modified">Ops Review — Source Modified</option>
              <option value="manually_handled">Manually Handled</option>
              <option value="failed">Failed</option>
            </select>
          </div>
        )}
        {filter === 'pending' && (
          <label style={{
            fontSize: 12, alignSelf: 'flex-end', paddingBottom: 6,
            display: 'flex', gap: 6, alignItems: 'center',
          }}>
            <input
              type="checkbox"
              checked={pushEligibleOnly}
              onChange={(e) => setPushEligibleOnly(e.target.checked)}
            />
            Sync-eligible only (has billable line items)
          </label>
        )}
        {filter === 'excluded_ref_data' && (
          <ExclusionReasonSelect
            value={exclusionReason}
            onChange={setExclusionReason}
            counts={facets?.exclusion_reasons || {}}
          />
        )}
      </div>

      {filter === 'gap' && (
        facetsLoading ? (
          // While facets are being re-fetched (e.g. after a date change) the
          // old gap_types counts are stale/wrong — hide them and show a
          // placeholder rather than flashing incorrect chips until the new
          // counts arrive.
          <div style={{ fontSize: 12, color: '#999', padding: '6px 2px' }}>
            Updating gap types…
          </div>
        ) : (
          <GapTypeChips
            gapTypes={facets?.gap_types}
            selected={gapTypes}
            onToggle={toggleGapType}
          />
        )
      )}
    </>
  );
}
